import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Globe, Search, ShoppingCart, Settings, Shield, RefreshCw, Lock, Unlock, ChevronRight, Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function EnomDomains() {
  const [status, setStatus] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTld, setSearchTld] = useState('com');
  const [searchResult, setSearchResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainInfo, setDomainInfo] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regForm, setRegForm] = useState({ sld: '', tld: 'com', years: 1, first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: '', zip: '', country: 'US' });
  const [registering, setRegistering] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statusRes, domainsRes] = await Promise.allSettled([
        api.get('/enom/status'),
        api.get('/enom/domains'),
      ]);
      if (statusRes.status === 'fulfilled') setStatus(statusRes.value.data);
      if (domainsRes.status === 'fulfilled') setDomains(domainsRes.value.data.domains || []);
    } catch {}
    setLoading(false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true); setSearchResult(null); setSuggestions([]);
    try {
      const [checkRes, suggestRes] = await Promise.allSettled([
        api.get('/enom/domains/check', { params: { sld: searchQuery, tld: searchTld } }),
        api.get('/enom/domains/suggest', { params: { sld: searchQuery, tld: searchTld } }),
      ]);
      if (checkRes.status === 'fulfilled') setSearchResult(checkRes.value.data);
      if (suggestRes.status === 'fulfilled') setSuggestions(suggestRes.value.data.suggestions || []);
    } catch {}
    setSearching(false);
  };

  const handleViewDomain = async (domainName) => {
    const [sld, ...tldParts] = domainName.split('.');
    const tld = tldParts.join('.');
    setSelectedDomain(domainName);
    try {
      const [infoRes, dnsRes] = await Promise.allSettled([
        api.get(`/enom/domains/${sld}/${tld}/info`),
        api.get(`/enom/domains/${sld}/${tld}/dns`),
      ]);
      if (infoRes.status === 'fulfilled') setDomainInfo(infoRes.value.data);
      if (dnsRes.status === 'fulfilled') setDnsRecords(dnsRes.value.data.records || []);
    } catch {}
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const res = await api.post('/enom/domains/register', regForm);
      if (res.data.success) {
        toast.success(`${regForm.sld}.${regForm.tld} registered!`);
        setRegisterOpen(false);
        loadData();
      } else {
        toast.error(res.data.error || res.data.rrp_text || 'Registration failed');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setRegistering(false);
  };

  const handleRenew = async (domainName) => {
    const [sld, ...tldParts] = domainName.split('.');
    const tld = tldParts.join('.');
    try {
      const res = await api.post(`/enom/domains/${sld}/${tld}/renew`, { years: 1 });
      if (res.data.success) { toast.success(`${domainName} renewed!`); loadData(); }
      else toast.error(res.data.error || 'Renewal failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleLock = async (domainName, lock) => {
    const [sld, ...tldParts] = domainName.split('.');
    const tld = tldParts.join('.');
    try {
      await api.post(`/enom/domains/${sld}/${tld}/lock`, { locked: lock });
      toast.success(`${domainName} ${lock ? 'locked' : 'unlocked'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6" data-testid="enom-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Globe className="h-6 w-6" /> Domain Management</h1>
          <p className="text-gray-500 mt-1">eNom domain registration, DNS, SSL, and management</p>
        </div>
        {status?.connected && (
          <Badge variant="custom" className="bg-green-100 text-green-800">Balance: ${status.balance}</Badge>
        )}
      </div>

      <Tabs defaultValue="search">
        <TabsList>
          <TabsTrigger value="search"><Search className="h-3.5 w-3.5 mr-1" /> Search</TabsTrigger>
          <TabsTrigger value="domains"><Globe className="h-3.5 w-3.5 mr-1" /> My Domains ({domains.length})</TabsTrigger>
        </TabsList>

        {/* SEARCH TAB */}
        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-2">
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Enter domain name..."
                  className="flex-1"
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  data-testid="domain-search-input"
                />
                <Select value={searchTld} onValueChange={setSearchTld}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['com','net','org','io','co','us','biz','info','dev','app','tech'].map(t => (
                      <SelectItem key={t} value={t}>.{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleSearch} disabled={searching} data-testid="domain-search-btn">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {searchResult && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {searchResult.available ? <CheckCircle className="h-6 w-6 text-green-500" /> : <XCircle className="h-6 w-6 text-red-500" />}
                    <div>
                      <p className="font-semibold text-lg">{searchResult.domain}</p>
                      <p className="text-sm text-gray-500">{searchResult.available ? 'Available for registration!' : 'Already taken'}</p>
                    </div>
                  </div>
                  {searchResult.available && (
                    <Button onClick={() => { setRegForm(p => ({ ...p, sld: searchQuery, tld: searchTld })); setRegisterOpen(true); }} data-testid="register-domain-btn">
                      <ShoppingCart className="h-4 w-4 mr-1" /> Register
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {suggestions.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Suggestions</CardTitle></CardHeader>
              <CardContent className="p-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                  {suggestions.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-gray-50 text-sm">
                      <span>{s}</span>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { const [sld, ...rest] = s.split('.'); setSearchQuery(sld); setSearchTld(rest.join('.')); handleSearch(); }}>
                        Check
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* DOMAINS TAB */}
        <TabsContent value="domains">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>Expiration</TableHead>
                    <TableHead>Lock</TableHead>
                    <TableHead>Auto-Renew</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domains.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No domains found</TableCell></TableRow>
                  ) : domains.map((d, i) => (
                    <TableRow key={i} className="cursor-pointer hover:bg-gray-50" onClick={() => handleViewDomain(d.name)}>
                      <TableCell className="font-semibold">{d.name}</TableCell>
                      <TableCell className="text-sm">{d.expiration}</TableCell>
                      <TableCell>
                        <Badge variant="custom" className={d.lock_status === 'Locked' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}>
                          {d.lock_status || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="custom" className={d.auto_renew === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                          {d.auto_renew || 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleRenew(d.name)}>Renew</Button>
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleViewDomain(d.name)}>
                            <Settings className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Domain Detail Dialog */}
      <Dialog open={!!selectedDomain} onOpenChange={open => { if (!open) setSelectedDomain(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> {selectedDomain}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="dns">
            <TabsList>
              <TabsTrigger value="dns">DNS Records</TabsTrigger>
              <TabsTrigger value="info">Domain Info</TabsTrigger>
            </TabsList>
            <TabsContent value="dns">
              {dnsRecords.length === 0 ? (
                <p className="text-gray-500 py-4 text-center text-sm">No DNS records found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Host</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Priority</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dnsRecords.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-sm">{r.hostname}</TableCell>
                        <TableCell><Badge variant="secondary">{r.record_type}</Badge></TableCell>
                        <TableCell className="font-mono text-xs max-w-[200px] truncate">{r.address}</TableCell>
                        <TableCell>{r.mxpref || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
            <TabsContent value="info">
              {domainInfo && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(domainInfo).filter(([k]) => !k.startsWith('_')).slice(0, 20).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b py-1">
                      <span className="text-gray-500">{k}:</span>
                      <span className="font-mono text-xs">{String(v).substring(0, 50)}</span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Registration Dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Register {regForm.sld}.{regForm.tld}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">First Name *</Label><Input value={regForm.first_name} onChange={e => setRegForm(p => ({...p, first_name: e.target.value}))} /></div>
              <div><Label className="text-xs">Last Name *</Label><Input value={regForm.last_name} onChange={e => setRegForm(p => ({...p, last_name: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Email *</Label><Input value={regForm.email} onChange={e => setRegForm(p => ({...p, email: e.target.value}))} /></div>
            <div><Label className="text-xs">Phone *</Label><Input value={regForm.phone} onChange={e => setRegForm(p => ({...p, phone: e.target.value}))} placeholder="+1.5551234567" /></div>
            <div><Label className="text-xs">Address *</Label><Input value={regForm.address} onChange={e => setRegForm(p => ({...p, address: e.target.value}))} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">City *</Label><Input value={regForm.city} onChange={e => setRegForm(p => ({...p, city: e.target.value}))} /></div>
              <div><Label className="text-xs">State *</Label><Input value={regForm.state} onChange={e => setRegForm(p => ({...p, state: e.target.value}))} /></div>
              <div><Label className="text-xs">ZIP *</Label><Input value={regForm.zip} onChange={e => setRegForm(p => ({...p, zip: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Years</Label>
                <Select value={String(regForm.years)} onValueChange={v => setRegForm(p => ({...p, years: parseInt(v)}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,5,10].map(y => <SelectItem key={y} value={String(y)}>{y} year{y>1?'s':''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Country</Label><Input value={regForm.country} onChange={e => setRegForm(p => ({...p, country: e.target.value}))} /></div>
            </div>
            <Button className="w-full" onClick={handleRegister} disabled={registering} data-testid="confirm-register-btn">
              {registering ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShoppingCart className="h-4 w-4 mr-1" />}
              Register Domain
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
